import type { SentienceLevel } from '@/types';

export interface DialogueToken {
  original: string;
  rendered: string;
  revealed: boolean;
}

export interface RenderOptions {
  sentienceLevel: SentienceLevel;
  seed?: number;
}

const GIBBERISH_CHARS = '@#!$%*&?+=~^';
const WHITESPACE_RE = /^\s+$/;

export class GibberishDialogueSystem {
  render(line: string, options: RenderOptions): DialogueToken[] {
    const tokens = line.split(/(\s+)/).filter((t) => t.length > 0);
    const rng = this.seededRng(options.seed ?? this.hash(line));
    const revealChance = this.revealChance(options.sentienceLevel);

    return tokens.map((token) => {
      if (WHITESPACE_RE.test(token)) {
        return { original: token, rendered: token, revealed: true };
      }
      if (options.sentienceLevel >= 4) {
        return { original: token, rendered: token, revealed: true };
      }
      if (rng() < revealChance) {
        return { original: token, rendered: token, revealed: true };
      }
      return {
        original: token,
        rendered: this.toGibberish(token, rng),
        revealed: false,
      };
    });
  }

  renderString(line: string, options: RenderOptions): string {
    return this.render(line, options)
      .map((t) => t.rendered)
      .join('');
  }

  private revealChance(level: SentienceLevel): number {
    switch (level) {
      case 0:
        return 0;
      case 1:
        return 0.05;
      case 2:
        return 0.2;
      case 3:
        return 0.45;
      case 4:
      default:
        return 1;
    }
  }

  private toGibberish(original: string, rng: () => number): string {
    let out = '';
    for (let i = 0; i < original.length; i++) {
      const c = original[i];
      if (/[.,!?;:"']/.test(c)) {
        out += c;
      } else {
        out += GIBBERISH_CHARS[Math.floor(rng() * GIBBERISH_CHARS.length)];
      }
    }
    return out;
  }

  private seededRng(seed: number): () => number {
    let s = seed === 0 ? 1 : seed;
    return () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
  }

  private hash(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (h * 31 + s.charCodeAt(i)) >>> 0;
    }
    return h || 1;
  }
}
